import React from 'react';
import { Empty, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ExperimentRow } from '@/types';
import { DASH, fmtNum, fmtUsd } from '../bq-insights/bqMath';
import { ciSide, fmtInterval, fmtPctN, fmtPoints } from './monetizationMath';
import type { TypedEntry } from './monetizationTabState';
import { titleWithInfo } from './tableHelpers';
import { TabBody } from './TabBody';

const { Text } = Typography;

const CI_COLOR = { positive: '#52c41a', negative: '#ff4d4f' } as const;

/** The 95% interval of the payer-rate difference: green when it excludes 0, '—' when unknown. */
export const CiCell: React.FC<{ row: ExperimentRow }> = ({ row }) => {
  const text = fmtInterval(row.diffCiLow, row.diffCiHigh);
  const side = ciSide(row.diffCiLow, row.diffCiHigh);
  if (side === null) return <span data-ci={text === DASH ? 'none' : 'includes-zero'}>{text}</span>;
  return (
    <span data-ci={`excludes-zero-${side}`} style={{ color: CI_COLOR[side], fontWeight: 600 }}>
      {text}
    </span>
  );
};

const retention = (v: number | null, n: number | null) =>
  v === null ? DASH : `${fmtPctN(v, 1)}${n === null ? '' : ` (n=${fmtNum(n)})`}`;

const columns: ColumnsType<ExperimentRow> = [
  { title: 'Experiment', dataIndex: 'experimentKey' },
  {
    title: 'Variant',
    dataIndex: 'variant',
    render: (v: string, r) => (
      <>
        {v}
        {r.isControl && (
          <Tag style={{ marginLeft: 6 }} data-control="1">
            control
          </Tag>
        )}
      </>
    ),
  },
  { title: 'Players', dataIndex: 'users', align: 'right', render: (v: number) => fmtNum(v) },
  { title: 'Payers', dataIndex: 'payers', align: 'right', render: (v: number) => fmtNum(v) },
  {
    title: 'Payer rate',
    dataIndex: 'payerRate',
    align: 'right',
    render: (v: number | null) => fmtPctN(v, 2),
  },
  {
    title: titleWithInfo('Payer-rate diff', 'Difference from control, in percentage points'),
    dataIndex: 'payerRateDiff',
    align: 'right',
    render: (v: number | null) => fmtPoints(v),
  },
  {
    title: titleWithInfo(
      'Payer-rate CI',
      '95% interval of the difference, in points. Green when it excludes 0 on the positive side, red on the negative side; "—" on control and when it cannot be computed.'
    ),
    align: 'right',
    render: (_, r) => <CiCell row={r} />,
  },
  {
    title: 'ARPDAU',
    dataIndex: 'arpdauUsd',
    align: 'right',
    render: (v: number | null) => (v === null ? DASH : fmtUsd(v, 4)),
  },
  {
    title: 'D1 retention',
    align: 'right',
    render: (_, r) => retention(r.d1Retention, r.d1Users),
  },
  {
    title: titleWithInfo('D1 diff', 'D1 retention minus control, in percentage points'),
    dataIndex: 'd1RetentionDiff',
    align: 'right',
    render: (v: number | null) => fmtPoints(v, 1),
  },
  {
    title: 'D7 retention',
    align: 'right',
    render: (_, r) => retention(r.d7Retention, r.d7Users),
  },
];

interface ContentProps {
  rows: ExperimentRow[];
}

const Content: React.FC<ContentProps> = ({ rows }) => {
  if (rows.length === 0) {
    return <Empty description="No experiment assignments" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }
  return (
    <Table<ExperimentRow>
      size="small"
      rowKey={(r) => `${r.experimentKey}|${r.variant}`}
      columns={columns}
      dataSource={rows}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  );
};

interface Props {
  entry: TypedEntry<ExperimentRow[]>;
  /** Experiment keys for the selector (from the experiments list). */
  keys: string[];
  /** The selected key; undefined = all experiments. */
  value?: string;
  onChange(key: string | undefined): void;
  onRetry?: () => void;
}

/** Variants against control. The range does not apply: the view covers the last 90 days. */
export const ExperimentsTab: React.FC<Props> = ({ entry, keys, value, onChange, onRetry }) => (
  <div>
    <Space style={{ marginBottom: 8 }} wrap>
      <Text type="secondary">Experiment</Text>
      <Select
        aria-label="Experiment"
        style={{ minWidth: 220 }}
        showSearch
        allowClear
        placeholder="All experiments"
        value={value}
        onChange={(k: string | undefined) => onChange(k)}
        options={keys.map((k) => ({ value: k, label: k }))}
      />
    </Space>
    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
      Over all assignments of the last 90 days; the date range above does not apply. Differences are
      against the control variant, in percentage points.
    </Text>
    <TabBody entry={entry} onRetry={onRetry}>
      {(rows) => <Content rows={rows} />}
    </TabBody>
  </div>
);
